import { validate as isUuid, v4 as uuidv4 } from 'uuid';
import { clientEvents } from './client-events';
import {
  captureKioskEvent,
  isKioskAnalyticsEnabled,
  type KioskAnalyticsEvent,
  type KioskAnalyticsProperties,
  setKioskVisitId,
} from './kiosk-analytics';
import { sentryService } from './sentry';

type PaymentStatus =
  | 'unknown'
  | 'authorized'
  | 'captured'
  | 'failed'
  | 'cancelled'
  | 'voucher';
type DeliveryStatus = 'unknown' | 'queued' | 'delivered' | 'partial' | 'failed';

interface Visit {
  id: string;
  sessionId: string | null;
  startedAt: number;
  lastSeenAt: number;
  step: string;
  stepStartedAt: number;
  sequence: number;
  paymentStatus: PaymentStatus;
  deliveryStatus: DeliveryStatus;
  milestones: KioskAnalyticsEvent[];
  properties: KioskAnalyticsProperties;
}

const STORAGE_KEY = 'kiosk_analytics_visit_v1';
const STEPS = new Set([
  'home',
  'art-style',
  'gdpr-consent',
  'photo-from-webcam',
  'photo-from-phone',
  'photo-prompt-from-phone',
  'faces',
  'portraits',
  'payment-type',
  'payment-terminal',
  'payment-voucher',
  'printing',
  'success',
  'print-failed',
  'gophoto-scan',
  'gophoto-upload',
]);
let visit: Visit | null = null;
let pageExitRegistered = false;

function persist(): void {
  try {
    if (visit) sessionStorage.setItem(STORAGE_KEY, JSON.stringify(visit));
    else sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // A locked-down kiosk still tracks this visit in memory.
  }
}

function readStoredVisit(): Visit | null {
  try {
    const stored: Visit | null = JSON.parse(
      sessionStorage.getItem(STORAGE_KEY) ?? 'null'
    );
    return stored &&
      typeof stored.id === 'string' &&
      isUuid(stored.id) &&
      typeof stored.startedAt === 'number' &&
      Number.isFinite(stored.startedAt) &&
      typeof stored.lastSeenAt === 'number' &&
      typeof stored.sequence === 'number' &&
      STEPS.has(stored.step) &&
      Array.isArray(stored.milestones) &&
      stored.properties &&
      typeof stored.properties === 'object'
      ? stored
      : null;
  } catch {
    return null;
  }
}

function isKioskPage(): boolean {
  const context = clientEvents.getContext();
  return Boolean(
    context.kiosk_slug &&
      context.route &&
      !context.route.startsWith('/customer/') &&
      !context.route.startsWith('/portraits')
  );
}

function record(
  event: KioskAnalyticsEvent,
  properties: KioskAnalyticsProperties = {},
  expectedVisitId: string | null = visit?.id ?? null
): void {
  if (!visit || visit.id !== expectedVisitId) return;
  visit.lastSeenAt = Date.now();
  visit.sequence++;
  captureKioskEvent(
    event,
    {
      ...visit.properties,
      ...properties,
      step: visit.step,
      elapsed_ms: Math.max(0, visit.lastSeenAt - visit.startedAt),
      event_sequence: visit.sequence,
    },
    visit.id
  );
  persist();
}

function end(reason: string): void {
  if (!visit) return;
  if (reason === 'print_failed' && visit.deliveryStatus === 'unknown')
    visit.deliveryStatus = 'failed';
  record('visit_ended', {
    end_reason: reason,
    last_step: visit.step,
    last_payment_status: visit.paymentStatus,
    delivery_status: visit.deliveryStatus,
    time_on_last_step_ms: Math.max(0, Date.now() - visit.stepStartedAt),
    observed_duration_ms: Math.max(0, visit.lastSeenAt - visit.startedAt),
    duration_complete: reason !== 'interrupted',
  });
  visit = null;
  setKioskVisitId(null);
  persist();
}

function activate(): void {
  if (!visit) return;
  setKioskVisitId(visit.id);
  sentryService.setTag('visit_id', visit.id);
  if (!pageExitRegistered) {
    window.addEventListener('pagehide', () =>
      record('visit_interrupted', { reason: 'page_exit' })
    );
    pageExitRegistered = true;
  }
}

function begin(properties: KioskAnalyticsProperties = {}): string | null {
  if (!(isKioskAnalyticsEnabled() && isKioskPage())) return null;
  visit ??= readStoredVisit();
  if (visit) end('interrupted');
  const now = Date.now();
  visit = {
    id: uuidv4(),
    sessionId: null,
    startedAt: now,
    lastSeenAt: now,
    step: 'home',
    stepStartedAt: now,
    sequence: 0,
    paymentStatus: 'unknown',
    deliveryStatus: 'unknown',
    milestones: [],
    properties: { entry: 'new', ...properties },
  };
  activate();
  record('visit_started');
  record('step_viewed', { previous_step: null });
  return visit.id;
}

function attachSession(sessionId: string): void {
  if (!visit) return;
  // Kept only in browser storage to match a reload. Never sent to PostHog.
  visit.sessionId = sessionId;
  persist();
}

function resume(
  sessionId: string,
  properties: KioskAnalyticsProperties = {}
): void {
  if (!(isKioskAnalyticsEnabled() && isKioskPage())) return;
  const stored = visit ?? readStoredVisit();
  if (stored?.sessionId === sessionId) {
    visit = stored;
    activate();
    record('visit_resumed');
  } else {
    begin({ ...properties, entry: 'resume' });
    attachSession(sessionId);
  }
}

function enterStep(step: string, locale: string | null = null): void {
  if (!(visit && STEPS.has(step)) || visit.step === step) return;
  if (step === 'home') {
    end('navigated_home');
    return;
  }
  const previousStep = visit.step;
  const previousDuration = Date.now() - visit.stepStartedAt;
  visit.step = step;
  visit.stepStartedAt = Date.now();
  record('step_viewed', {
    previous_step: previousStep,
    previous_step_duration_ms: Math.max(0, previousDuration),
    locale,
  });
}

function once(
  event: KioskAnalyticsEvent,
  properties: KioskAnalyticsProperties = {},
  expectedVisitId = visit?.id ?? null
): void {
  if (
    !visit ||
    visit.id !== expectedVisitId ||
    visit.milestones.includes(event)
  )
    return;
  visit.milestones.push(event);
  record(event, properties, expectedVisitId);
}

export const kioskJourney = {
  get id(): string | null {
    return visit?.id ?? null;
  },
  begin,
  attachSession,
  resume,
  enterStep,
  record,
  once,
  end,
  payment(
    status: PaymentStatus,
    properties: KioskAnalyticsProperties = {},
    expectedVisitId = visit?.id ?? null
  ): void {
    if (!visit || visit.id !== expectedVisitId) return;
    visit.paymentStatus = status;
    record(
      'payment_result',
      { ...properties, payment_status: status },
      expectedVisitId
    );
  },
  delivery(
    status: DeliveryStatus,
    properties: KioskAnalyticsProperties = {},
    expectedVisitId = visit?.id ?? null
  ): void {
    if (!visit || visit.id !== expectedVisitId) return;
    visit.deliveryStatus = status;
    record(
      'delivery_result',
      { ...properties, delivery_status: status },
      expectedVisitId
    );
  },
};
